"use client";

import * as React from "react";

import { fields } from "@keystatic/core";
import { block } from "@keystatic/core/content-components";

/**
 * Native Keystatic block component for CDN-hosted images.
 *
 * Uploads to DigitalOcean Spaces via /api/admin/cms/upload-image and stores
 * only the resulting public CDN URL in the `.mdoc` source. Two ways to
 * insert an image:
 *
 *   1. Drag and drop an image file directly onto the editor surface.
 *      Keystatic calls `handleFile` on every registered block; the first one
 *      that returns a non-`false` value wins. Ours accepts any image MIME.
 *
 *   2. Use the "+" menu in the editor and pick "CDN image". The block opens
 *      with empty fields and the NodeView shows a click-to-upload prompt.
 *
 * The schema fields are still individually editable in Keystatic's sidebar
 * (alt, caption, width, height) — the NodeView just renders a friendly
 * preview block in the editor.
 */

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

const SCHEMA = {
  src: fields.url({
    label: "Image URL",
    description: "Auto-filled when you drag an image into the editor.",
  }),
  alt: fields.text({
    label: "Alt text",
    description: "Describe the image for screen readers and SEO.",
  }),
  caption: fields.text({
    label: "Caption",
    description: "Optional caption shown under the image.",
  }),
  width: fields.integer({
    label: "Width (px)",
    description: "Optional intrinsic width.",
  }),
  height: fields.integer({
    label: "Height (px)",
    description: "Optional intrinsic height.",
  }),
} as const;

type CdnImageValue = {
  src: string | null;
  alt: string;
  caption: string;
  width: number | null;
  height: number | null;
};

async function uploadFile(file: File): Promise<string | null> {
  if (!ALLOWED_TYPES.includes(file.type)) return null;

  const form = new FormData();
  form.append("file", file);
  form.append("contentType", "generic");

  let res: Response;
  try {
    res = await fetch("/api/admin/cms/upload-image", {
      method: "POST",
      body: form,
    });
  } catch {
    return null;
  }

  if (!res.ok) return null;

  try {
    const json = (await res.json()) as { ok?: boolean; url?: string };
    return json?.url ?? null;
  } catch {
    return null;
  }
}

function altFromFilename(name: string | undefined): string {
  if (!name) return "";
  return name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
}

const CdnImageNodeView: React.FC<{
  value: CdnImageValue;
  onChange: (next: CdnImageValue) => void;
  onRemove: () => void;
  isSelected: boolean;
}> = ({ value, onChange, isSelected }) => {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFiles = React.useCallback(
    async (files: FileList | File[] | null | undefined) => {
      const file = files?.[0];
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError("Only PNG, JPEG, WebP, or GIF images are supported.");
        return;
      }
      setError(null);
      setUploading(true);
      const url = await uploadFile(file);
      setUploading(false);
      if (!url) {
        setError("Upload failed. Make sure you're signed in as ADMIN.");
        return;
      }
      onChange({
        ...value,
        src: url,
        alt: value.alt || altFromFilename(file.name),
      });
    },
    [onChange, value],
  );

  const onPickClick = () => inputRef.current?.click();

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void handleFiles(e.dataTransfer?.files);
  };

  const stop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const containerStyle: React.CSSProperties = {
    border: isSelected ? "2px solid #16a34a" : "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    margin: "1rem 0",
    background: "#fafafa",
  };

  if (value.src) {
    return (
      <div style={containerStyle}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={value.src}
          alt={value.alt || ""}
          style={{ display: "block", width: "100%", height: "auto" }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.5rem 0.75rem",
            fontSize: 12,
            color: "#525252",
            borderTop: "1px solid #e5e7eb",
            background: "#ffffff",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value.caption || value.alt || "CDN image"}
          </span>
          <button
            type="button"
            onClick={onPickClick}
            disabled={uploading}
            style={{
              border: "1px solid #d4d4d4",
              borderRadius: 6,
              padding: "2px 8px",
              background: uploading ? "#f5f5f5" : "#ffffff",
              cursor: uploading ? "not-allowed" : "pointer",
              fontSize: 12,
            }}
          >
            {uploading ? "Uploading\u2026" : "Replace"}
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={(e) => void handleFiles(e.target.files)}
        />
        {error && (
          <div style={{ padding: "0.5rem 0.75rem", color: "#b91c1c", fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onDragOver={stop}
      onDragEnter={stop}
      onDrop={onDrop}
      onClick={onPickClick}
      style={{
        ...containerStyle,
        padding: "2rem",
        textAlign: "center",
        cursor: "pointer",
        color: "#525252",
      }}
    >
      <div style={{ fontSize: 14, marginBottom: 6 }}>
        {uploading ? "Uploading\u2026" : "Drop an image here or click to upload"}
      </div>
      <div style={{ fontSize: 12, color: "#737373" }}>
        PNG, JPEG, WebP, or GIF \u00b7 up to 10MB \u00b7 stored on
        DigitalOcean CDN
      </div>
      {error && (
        <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 12 }}>{error}</div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => void handleFiles(e.target.files)}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const cdnImageBlock = block({
  label: "CDN image",
  description: "Drag-and-drop image stored on DigitalOcean CDN.",
  schema: SCHEMA,
  NodeView: CdnImageNodeView,
});

/**
 * Keystatic's `BlockComponent` type exposes `handleFile`, but the `block(...)`
 * factory's parameter type doesn't accept it inline. We attach it post-hoc
 * with a narrow cast so dragging an image directly onto the editor surface
 * triggers an upload and inserts a `CdnImage` block.
 *
 * Returning `false` tells Keystatic "this component does not handle this
 * file" so other components (or the default behaviour) can take over.
 */
type CdnImageHandleFile = (
  file: File,
) => Promise<CdnImageValue | false> | (CdnImageValue | false);

(cdnImageBlock as unknown as { handleFile: CdnImageHandleFile }).handleFile =
  async (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) return false;
    const url = await uploadFile(file);
    if (!url) return false;
    return {
      src: url,
      alt: altFromFilename(file.name),
      caption: "",
      width: null,
      height: null,
    };
  };

export const cdnImageComponent = cdnImageBlock;
